<template>
  <div class="customers-table">
    <CommunicationComposeModal
      v-if="showComposeModal"
      @close="closeCommunicationModal"
      :preset-recipient="communicationRecipientPreset"
    />

    <SetColorAndLogoReminder
      v-if="!currentAccountantCompany.accountant_branding.color || !currentAccountantCompany.accountant_branding.logo"
    />

    <FilterBar
      cookieName="AccountantConnectionsFilterOptionsVue"
      :mainFilters="['SearchFilter', 'AccountantConnectionStatusFilter', 'AccountantCustomerPremiumFilter']"
      filterBarType="accountant-connections"
      @change="filtersChanged"
    >
      <template v-slot:right-bar>
        <TotalAmountsAccountants ref="total_amounts_accountants" />
      </template>
    </FilterBar>

    <div class="content--inner">
      <overlay-scrollbars :options="scrollBarsOptions" class="customers-table-scrollbar">
        <b-table
          :items="items"
          :fields="fields"
          @row-clicked="customerClicked"
          striped
          hover
          :class="['gk-table gk-table--responsive gk-table--striped gk-table--hover']"
          :busy="loading"
          show-empty
          table-busy
        >
          <template #empty="">
            <span class="empty-table-text" v-translate>No connections found.</span>
          </template>
          <template #table-busy="">
            <b-skeleton-table
              :rows="10"
              :columns="4"
              :table-props="{ bordered: false, striped: true }"
              :hide-header="true"
            />
          </template>
          <template #cell(name)="data">
            <div class="avatar-cell">
              <b-avatar
                class="avatar"
                :src="data.item.customer.logo ? data.item.customer.logo : ''"
                :text="$gettext('No logo')"
                size="40px"
              />
            </div>
            <div v-if="data.item.name" class="company-name-cell">
              <span>{{ data.item.name }}</span>
            </div>
            <div v-else class="company-name-cell">
              <span class="text-unassigned" style="font-style: italic;">{{ $gettext('No company name') }}</span>
            </div>
          </template>

          <template #cell(customer_name)="data">
            <div class="customer-name-cell">
              <span>{{ data.item.customer_name }}</span>
              <small class="text-unassigned">{{ data.item.customer_email }}</small>
            </div>
          </template>

          <template #cell(company_email)="data">
            <div v-if="data.item.company_email">
              <span>{{ data.item.company_email }}</span>
            </div>
            <div v-else>
              <span class="text-unassigned" style="font-style: italic;">{{ $gettext('No company email') }}</span>
            </div>
          </template>

          <template #cell(created)="data">
            {{ data.item.created | formatDate }}
          </template>
          <template #cell(premium)="data">
            <div class="premium-cell">
              <div v-if="data.item.customer.is_premium">
                <GkBadge
                  v-if="getSubscriptionDuration(data.item) > 1"
                  :text="$gettext('Premium')"
                  account-badge no-shadow
                  :tooltip-text="$gettext('Your client has a yearly Premium subscription. He will receive a 30% discount and you will receive a yearly referral fee of €10. Every year for as long as your client stays connected.')"
                  type="premium"
                />
                <GkBadge
                  v-if="getSubscriptionDuration(data.item) <= 1"
                  :text="$gettext('Premium')"
                   no-shadow
                  :tooltip-text="$gettext('This client has a monthly Premium subscription but does not take advantage of your 30% discount and you do not receive a referral fee. If your client switches to a yearly subscription, not only will he receive a 30% discount but you will also receive a yearly referral fee of €10.')"
                  type="premium"
                  variant="brand-ghost"
                />
              </div>

              <div v-if="!data.item.customer.is_premium">
                <GkBadge
                  :text="$gettext('Basic')"
                  account-badge no-shadow
                  :tooltipText="$gettext('This client has a Basic account and you will not receive a referral fee. If this person upgrades to a yearly Premium account, your client receives a 30% discount while you will receive a yearly referral fee of €10. Try to upgrade your client.')"
                  type="basic"
                />
              </div>
            </div>
          </template>
          <template #cell(status)="data">
            <div class="status-cell">
              <StatusBadge :display-status="{ detailed_status: data.item.status }" />
            </div>
          </template>
          <template #cell(actions)="data">
            <b-dropdown class="icon-toggle ui-dashboard-menu-dropdown" variant="link" right no-caret>
              <template #button-content>
                <font-awesome-icon icon="fa-solid fa-ellipsis-vertical" />
              </template>
              <b-dropdown-item v-if="isConnected(data.item)" :to="{ name: 'ControlPanelSummary', params: { customerId: data.item.customer.id } }" data-analytics-click="customer list dropdown: control panel">
                <font-awesome-icon icon="fa-regular fa-rectangles-mixed" />
                <span>{{ $gettext('Control Panel') }}</span>
              </b-dropdown-item>
              <b-dropdown-item v-if="isConnected(data.item)" @click="openSeeAccount(data.item.customer.id)" data-analytics-click="customer list dropdown: see account">
                <font-awesome-icon icon="fa-regular fa-eye" />
                <span>{{ $gettext('See Account') }}</span>
              </b-dropdown-item>
              <b-dropdown-item v-if="isConnected(data.item)" @click="openCommunicationModal(data.item)" data-analytics-click="customer list dropdown: send message">
                <font-awesome-icon icon="fa-regular fa-comment" />
                <span>{{ $gettext('Send message') }}</span>
              </b-dropdown-item>
              <b-dropdown-divider v-if="isConnected(data.item)" />
              <DropdownItemDelete
                v-if="isConnected(data.item)"
                :item="data.item.customer"
                :item-type="'accountant_connection'"
                :item-name="data.item.name"
                @item-deleted="onDisconnected"
              />
              <b-dropdown-item v-else-if="data.item.pending_invite" @click="sendReminder(data.item)">
                <div class="text-blue">
                  <font-awesome-icon icon="fa-solid fa-paper-plane" />
                  <span>{{ $gettext('Send reminder') }}</span>
                </div>
              </b-dropdown-item>
              <b-dropdown-item v-else @click="reconnect(data.item.customer.id)">
                <div class="text-blue">
                  <font-awesome-icon icon="fa-solid fa-link" />
                  <span>{{ $gettext('Reconnect') }}</span>
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <template slot="bottom-row" v-if="nextListUrl">
            <td :colspan="fields.length" class="table-load-more">
              <GekkoButton 
                v-if="nextListUrl && !fetchingCustomers" 
                @click="handleScroll()"
                id="load_more_button" 
                class="text-info"
                variant="link" 
                :text="$gettext('Load more customers')" 
              />
              <LoadingSpinner v-if="fetchingCustomers" />
            </td>
          </template>
        </b-table>
      </overlay-scrollbars>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import FilterBar from '@/components/list_filters/FilterBar'
import GekkoButton from '@/components/GekkoButton'
import TotalAmountsAccountants from '@/components/accountants/TotalAmountsAccountants'
import SetColorAndLogoReminder from '@/components/accountants/SetColorAndLogoReminder.vue'
import DropdownItemDelete from '@/components/ui/DropdownItemDelete'
import StatusBadge from '@/components/invoices/ui/StatusBadge'
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"
import CommunicationComposeModal from '@/components/accountants/communication/compose/CommunicationComposeModal.vue'
import GkBadge from '@/components/GkBadge.vue'
import AccountantConnectionService from '@/store/services/accountants.service'

export default {
  name: 'CustomerList',
  components: {
    FilterBar,
    GekkoButton,
    TotalAmountsAccountants,
    SetColorAndLogoReminder,
    DropdownItemDelete,
    StatusBadge,
    LoadingSpinner,
    CommunicationComposeModal,
    GkBadge
  },
  data() {
    return {
      items: [],
      fields: [
        { key: 'name', tdClass: 'avatar-col' },
        { key: 'customer_name' },
        { key: 'company_email' },
        { key: 'premium', tdClass: '', thClass: 'text-center' },
        { key: 'status', tdClass: 'col-status', thClass: 'text-center' },
        { key: 'actions', tdClass: 'col-actions', thClass: 'text-center' },
      ],
      currentFilters: null,
      loading: true,
      nextListUrl: null,
      fetchingCustomers: false,
      scrollBarsOptions: {
        callbacks : {
          onScrollStop: this.handleScroll
        }
      },
      showComposeModal: false,
      communicationRecipientPreset: null
    }
  },
  computed: {
    ...mapGetters({
      currentAccountantCompany: 'accountants/getCurrentAccountantCompany',
      getCompanyId: 'main/getCompanyId',
      preferredLanguage: 'main/getPreferredLanguage',
    }),
  },
  created() {
  },
  methods: {
    ...mapActions({
      reconnectCustomer: 'accountants/reconnectCustomer',
      getPendingInvites: 'accountants/getPendingInvites',
      getAccountantConnections: 'accountants/getAccountantConnections',
      getAccountantConnection: 'accountants/getAccountantConnection',
      getAccountantNextListConnections: 'accountants/getAccountantNextListConnections',
    }),
    getSubscriptionDuration(item) {
      return item.customer?.accountant_branding?.subscription_duration
    },
    customerClicked(item) {
      if (this.isConnected(item)) {
        this.$router.push({ name: 'ControlPanelSummary', params: { customerId: item.customer.id } })
      }
    },
    filtersChanged(filters) {
      this.currentFilters = filters
      this.loading = true
      this._getAccountantConnections(filters)
    },
    refreshConnections() {
      this.filtersChanged(this.currentFilters)
      this.$refs.total_amounts_accountants.getTotalAmounts()
    },
    _getAccountantConnections(filters) {
      this.getAccountantConnections(filters).then((response) => {
        this.items = response.data.results
        this.items = this.items.map((item) => {
          item['customer_name'] = item.customer.created_by.display_name
          item['customer_email'] = item.customer.created_by.email
          item['company_email'] = item.customer.email
          return item
        })
        this.nextListUrl = response.data.next
        this.loading = false
        this.setPendingStatus()
      })
    },
    setPendingStatus() {
      this.getPendingInvites().then((response) => {
        this.pendingInvites = response.data
        this.items.forEach((item) => {
          if (this.pendingInvites.find((o) => o.invite_to === item.customer_email && item.status !== 'connected'))
            item.status = 'pending'
        })
      })
    },
    onDisconnected(accountantConnection) {
      for (var i = 0; i < this.items.length; i++) {
        if (this.items[i].id === accountantConnection.id) {
          this.$set(this.items, i, accountantConnection)
          break
        }
      }
      this.refreshConnections()
    },
    isConnected(item) {
      return (item.status === 'connected')
    },
    isInViewport(element_id) {
      let element = document.getElementById(element_id)
      if (!element) return false
      let rect = element.getBoundingClientRect()
      let html = document.documentElement
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || html.clientHeight) &&
        rect.right <= (window.innerWidth || html.clientWidth)
      )
    },
    handleScroll() {
      if (!this.fetchingCustomers && this.nextListUrl) {
        let loadNewCustomers = this.isInViewport('load_more_button')

        if (loadNewCustomers) {
          this.fetchingCustomers = true
          this.getAccountantNextListConnections(this.nextListUrl).then((response) => {
            this.fetchingCustomers = false
            let newItems = response.data.results.map((item) => {
              item['customer_name'] = item.customer.created_by.display_name
              item['customer_email'] = item.customer.created_by.email
              item['company_email'] = item.customer.email
              return item
            })
            this.items = this.items.concat(newItems)
            this.nextListUrl = response.data.next
          })
        }
      }
    },
    addGekkoConnection(item) {
      this.getAccountantConnection(item.id).then((response) => {
        const new_connection = response.data
        const index = this.items.findIndex(existing => existing.id === new_connection.id)
        if (index !== -1) {
          // Update existing item reactively
          this.$set(this.items[index], 'status', 'connected')
        } else {
          // Add new item to the beginning of the list
          new_connection['customer_name'] = new_connection.customer.created_by.display_name
          new_connection['customer_email'] = new_connection.customer.created_by.email
          new_connection['company_email'] = new_connection.customer.email
          this.items.unshift(new_connection)
        }
      })
    },
    openSeeAccount(customer_id) {
      // TODO this
      window.location.href = '/' + this.preferredLanguage + '/dashboard?vc_id=' + customer_id
    },
    reconnect(customer_id) {
      this.reconnectCustomer({ customer_id: customer_id, company_id: this.getCompanyId, is_gekko_accountant: this.currentAccountantCompany.accountant_branding.is_gekko_accountant }).then((response) => {
        this.refreshConnections()
      })
    },
    openCommunicationModal(item) {
      this.communicationRecipientPreset = item.customer
      this.showComposeModal = true
    },
    closeCommunicationModal () {
      this.communicationRecipientPreset = null
      this.showComposeModal = false
    },
    sendReminder(item) {
      AccountantConnectionService.resendInvite(item.pending_invite.id).then(() => {
        this.$store.commit('setAlert', {
          title: this.$gettext('Reminder sent'),
          text: this.$gettext('You have successfully sent a reminder to your customer to accept the invite.'),
          variant: 'success',
          duration: 4
        })
      }).catch((error) => {
        this.$store.commit('setAlert', {
          title: this.$gettext('Failed to send reminder'),
          text: this.$gettext('There was an error sending the reminder. Please try again later.'),
          variant: 'danger',
          duration: 4
        })
      })
    }
  },
}
</script>

<style lang="scss">
.customers-table {
  .avatar-col {
    max-width: 300px;
    display: flex;
    align-items: center;
    .avatar-cell {
      min-width: 50px;
      display: inline;
    }
    .company-name-cell {
      min-width: 50px;
      display: inline;

      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
    }
  }
  .customer-name-cell {
    display: flex;
    flex-direction: column;
  }
  .status-cell {
    justify-content: center;
  }
  .premium-cell {
    font-size: 12px;
  }
  .col-status {
    width: 150px;
    min-width: 150px;
  }
}

</style>

<style lang="scss" scoped>
.customers-table {
  .os-host.customers-table-scrollbar {
    min-height: 360px;
  }
  &.table-on-modal {
    .os-host.customers-table-scrollbar {
      height: 300px !important;
      max-height: 300px;
      .os-content {
        padding: 0 !important;
      }
    }
  }
}
.empty-table-text {
  color: $colorEmptyList;
  font-style: italic;
}
</style>